"use client";

import { Lang, langs } from "@/langs";
import { resolveHref } from "next/dist/client/resolve-href";
import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";
import Router from "next/router";
import { useContext, useEffect, useTransition } from "react";
import { PageTransitionContext } from "./PageTransitionProvider";

/**
 * A custom Link component that wraps Next.js's next/link component for page transition indicator.
 * https://github.com/vercel/next.js/discussions/41934
 */
export default function Link({
  href,
  children,
  replace,
  className,
  lang: langProp,
  withTransition = true,
  ...rest
}: Parameters<typeof NextLink>[0] & { lang?: Lang; withTransition?: boolean }) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [_, setIsTransitioning] = useContext(PageTransitionContext);
  useEffect(() => {
    if (withTransition) {
      setIsTransitioning(isPending);
    }
  }, [isPending, setIsTransitioning, withTransition]);

  const pathname = usePathname();
  const lang = langProp ?? langs.find((l) => pathname.startsWith(`/${l}`));

  const hrefWithLang =
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    lang && lang !== "en" && !String(href).startsWith(`/${lang}`)
      ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
        `/${lang}${String(href)}`
      : href;

  return (
    <NextLink
      href={hrefWithLang}
      onClick={(e) => {
        // Allow default behavior for modifier key clicks (new tab, etc)
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }

        if (withTransition) {
          e.preventDefault();
          startTransition(() => {
            const [, resolvedHref] = resolveHref(Router, href, true);
            if (!resolvedHref) return;
            if (replace) {
              router.replace(resolvedHref);
            } else {
              router.push(resolvedHref);
            }
          });
        }
      }}
      className={className}
      {...rest}
    >
      {children}
    </NextLink>
  );
}
